import {
	ApplicationRef,
	ComponentRef,
	createComponent,
	inject,
	Injectable,
	Injector,
	signal,
	Type,
} from "@angular/core";
import { ConfirmComponent } from "common/dialog/confirm.component";
import { DialogAdapter } from "common/dialog/dialog-adapter";
import { DialogComponent, DialogConfig } from "common/dialog/dialog.component";
import { MessageDetails, WarnComponent } from "common/dialog/warn.component";
import { WorkspaceFilter, WorkspaceSelectionDialogComponent } from "common/dialog/workspace-selection-dialog.component";
import { WorkspaceSelected } from "common/workspace/workspace-selection.component";
import { ErrorPopupComponent } from "common/dialog/error-popup.component";

@Injectable({ providedIn: "root" })
export class DialogService {
	private applicationRef = inject(ApplicationRef);
	private injector = inject(Injector);

	private dialogRegistry = new Map<string, DialogComponent<unknown>>();

	async open<T>(
		dialogId: string,
		options: {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Remove this comment and fix warnings!
			component: Type<any>;
			componentArgs: Record<string, unknown>;
			defaultResult: T;
			dialogConfig?: DialogConfig;
		},
	): Promise<T> {
		const dialogAdapter = new DialogAdapter();
		const contentComponentInjector = Injector.create({
			providers: [{ provide: DialogAdapter, useValue: dialogAdapter }],
			parent: this.injector,
			name: "contentComponentInjector",
		});
		const contentComponentRef = createComponent(options.component, {
			environmentInjector: this.applicationRef.injector,
			elementInjector: contentComponentInjector,
		});
		if (options.componentArgs) {
			Object.keys(options.componentArgs).forEach((arg: string) => {
				contentComponentRef.instance[arg] = signal(options.componentArgs[arg]);
			});
		}

		this.applicationRef.attachView(contentComponentRef.hostView);
		const dialogRef = createComponent(DialogComponent, {
			environmentInjector: this.applicationRef.injector,
			elementInjector: contentComponentInjector,
			projectableNodes: [[contentComponentRef.location.nativeElement]],
		});

		this.applicationRef.attachView(dialogRef.hostView);
		document.body.append(dialogRef.location.nativeElement);

		this.dialogRegistry.set(dialogId, dialogRef.instance);
		const dialogPromise = (dialogRef.instance as DialogComponent<T>).show(
			dialogRef as ComponentRef<DialogComponent<T>>,
			contentComponentRef,
			options.defaultResult,
			options.dialogConfig ?? { size: "small", autoHeight: false },
		);

		dialogPromise.then(() => {
			this.close(dialogId);
		});
		return dialogPromise;
	}

	close(dialogId: string): void {
		const dialog = this.dialogRegistry.get(dialogId);
		if (dialog) {
			this.dialogRegistry.delete(dialogId);
			dialog.close();
		}
	}

	async confirm(
		messageKey: string,
		translateValues?: Record<string, number | string>,
		okKey: string = "delete",
		cancelKey: string = "cancel",
		isDeleteButton: boolean = false,
		detailsKey?: string,
		size: "large" | "medium" | "small" = "small",
	): Promise<boolean> {
		return (
			(await this.confirmImpl(messageKey, okKey, cancelKey, isDeleteButton, translateValues, detailsKey, size)) === "ok"
		);
	}

	async warn(
		message: string,
		messageDetails?: MessageDetails | null,
		title?: string,
		okKey: string = "ok",
		size: "large" | "medium" | "small" = "small",
	): Promise<boolean> {
		return (await this.warnImpl(message, messageDetails, title, okKey, size)) === "ok";
	}

	async error(titleKey: string, message: string, size: "large" | "medium" | "small" = "small"): Promise<boolean> {
		return (await this.errorImpl(titleKey, message, size)) === "ok";
	}

	private async confirmImpl(
		messageKey: string,
		okKey: string,
		cancelKey: string,
		isDeleteButton: boolean,
		translateValues?: unknown,
		detailsKey?: string,
		size: "large" | "medium" | "small" = "small",
	): Promise<string> {
		return this.open<string>("confirm", {
			component: ConfirmComponent,
			componentArgs: {
				keys: {
					message: messageKey,
					ok: okKey,
					cancel: cancelKey,
					isDeleteButton: isDeleteButton,
					values: translateValues ?? {},
					details: detailsKey,
				},
			},
			defaultResult: "cancel",
			dialogConfig: {
				size: size,
				autoHeight: false,
			},
		});
	}

	private async warnImpl(
		message: string,
		messageDetails?: MessageDetails | null,
		title?: string,
		okKey: string = "ok",
		size: "large" | "medium" | "small" = "small",
		autoheight: boolean = true,
	): Promise<string> {
		return this.open<string>("warn", {
			component: WarnComponent,
			componentArgs: {
				keys: {
					title: title,
					message: message,
					messageDetails: messageDetails,
					ok: okKey,
				},
			},
			defaultResult: "cancel",
			dialogConfig: {
				size: size,
				autoHeight: autoheight,
			},
		});
	}

	private async errorImpl(
		titleKey: string,
		message: string,
		size: "large" | "medium" | "small" = "small",
		autoheight: boolean = true,
	): Promise<string> {
		return this.open<string>("error", {
			component: ErrorPopupComponent,
			componentArgs: {
				keys: {
					titleKey: titleKey,
					message: message,
				},
			},
			defaultResult: "ok",
			dialogConfig: {
				size: size,
				autoHeight: autoheight,
			},
		});
	}

	selectWorkspace(
		okFn: (workspace: WorkspaceSelected) => void,
		cancelFn: () => void,
		workspaceFilter?: WorkspaceFilter,
	): void {
		void this.open<{
			value: string;
			workspace?: WorkspaceSelected;
		}>("dialog,service.workspace.selection", {
			component: WorkspaceSelectionDialogComponent,
			componentArgs: {
				filter: workspaceFilter ?? {},
			},
			defaultResult: {
				value: "cancel",
			},
			dialogConfig: {
				size: "large",
				autoHeight: false,
			},
		}).then((result) => {
			if (result.value === "ok") {
				okFn(result.workspace!);
			} else if (cancelFn) {
				cancelFn();
			}
		});
	}
}
